

/* ==========================================================================
   CENTRAL APPLICATION STATE (/database/state.js)
   ========================================================================== */

import '/assets/shared/sff-contract.js?v=20260822-routebridge3';

const COMPONENT_STATE_KEYS = Object.freeze({
  gpu: 'activeSelectedGpuSpecs',
  cpu: 'activeSelectedCpuCoolerSpecs',
  aio: 'activeSelectedAioSpecs',
  mobo: 'activeSelectedMoboSpecs',
  psu: 'activeSelectedPsuSpecs',
  ram: 'activeSelectedRamSpecs'
});

export const state = {
  activeSelectedCpuCoolerSpecs: null,
  activeSelectedAioSpecs: null,
  activeSelectedGpuSpecs: null,
  activeSelectedMoboSpecs: null,
  activeSelectedPsuSpecs: null,
  activeSelectedRamSpecs: null,
  // Exact imported PSU/RAM refs are retained even where a visible control is form-factor
  // based. Once the user explicitly changes the control the corresponding exact identity
  // is cleared, preventing stale IDs from leaking into shared build tokens.
  portableBuildHardware: { psu: null, ram: null },
  portableFixedStep: null,
  hardwareGlobal: null, // Stores raw hardware database array/object
  initialShortcodeUrl: null,
  lastHydratedCaseSlug: null,
  isRestoringShortcodeState: false,
  routeNavigationIntent: 'replace',
  // Tokenized /db build URLs are immutable snapshots until the user explicitly
  // starts a different case/build navigation transaction. This flag lets the
  // router distinguish that intent from ordinary compatibility re-evaluation.
  portableRouteExitRequested: false,
  sffPinnedCases: [], // Array storing pinned case indices [idxA, idxB]
  fetchedCasesGlobal: [],

  // Runtime lookup indexes. These are rebuilt only when the catalog is hydrated,
  // avoiding repeated O(n) slug/result scans in routing, cards and telemetry.
  caseBySlug: new Map(),
  caseIndexBySlug: new Map(),
  caseByNormalizedName: new Map(),
  resultByIdx: new Map(),

  activeAuditReportData: null,
  activeAuditTargetCaseIdx: null,
  activeAuditTargetCaseName: "",
  activeAuditReportHash: "",
  activeAuditRequestPayload: null,
  
  // Dynamic Mode / Layout Overrides
  selectedModeId: "default", // Currently active mode ID for selected case evaluation
  caseModeSelections: {},   // Key-value store mapping [stable case key] -> selectedModeId
  
  // Runtime filters & UI flags
  activeLayout: "all",
  filterFlags: { hideIncompatible: false, hideLegacy: false },
  visibleLimit: 50,
  selectedCaseIdx: null,
  filteredResults: [],
  totalFitsCount: 0
};

function clonePlain(value) {
  if (!value || typeof value !== 'object') return value;
  if (typeof structuredClone === 'function') {
    try { return structuredClone(value); } catch (_) { /* JSON fallback below */ }
  }
  return JSON.parse(JSON.stringify(value));
}

function exposeLegacyWindowState(key, value) {
  if (typeof window !== 'undefined') window[key] = value;
}

function normalizeCaseSlug(value) {
  if (!value) return '';
  const normalized = window.SFFContracts?.normalizeRouteSlug?.(value);
  return normalized || window.SFFContracts?.fallbackSlug?.(value) || '';
}

function canonicalCaseSlug(caseObj) {
  return window.SFFContracts?.caseRouteSlug?.(caseObj) || normalizeCaseSlug(
    caseObj?._slug || caseObj?.case_slug || caseObj?.db_slug || caseObj?.slug || caseObj?.name || ''
  );
}

export function rebuildCaseLookupIndexes(cases = state.fetchedCasesGlobal) {
  state.caseBySlug.clear();
  state.caseIndexBySlug.clear();
  state.caseByNormalizedName.clear();

  if (!Array.isArray(cases)) return;
  cases.forEach((caseObj, idx) => {
    if (!caseObj || !caseObj.name) return;
    const canonicalSlug = canonicalCaseSlug(caseObj);
    if (canonicalSlug && !state.caseBySlug.has(canonicalSlug)) {
      state.caseBySlug.set(canonicalSlug, caseObj);
      state.caseIndexBySlug.set(canonicalSlug, idx);
    }

    // Preserve alternate explicit slugs as aliases without replacing the canonical mapping.
    [caseObj.slug, caseObj.case_slug, caseObj.db_slug].forEach(rawSlug => {
      const alias = normalizeCaseSlug(rawSlug);
      if (!alias || state.caseBySlug.has(alias)) return;
      state.caseBySlug.set(alias, caseObj);
      state.caseIndexBySlug.set(alias, idx);
    });

    const nameKey = normalizeCaseIdentityName(caseObj.name);
    if (nameKey && !state.caseByNormalizedName.has(nameKey)) {
      state.caseByNormalizedName.set(nameKey, { caseObj, idx });
    }
  });
}

export function getCaseBySlug(slug) {
  return state.caseBySlug.get(normalizeCaseSlug(slug)) || null;
}

export function getCaseIndexBySlug(slug) {
  const idx = state.caseIndexBySlug.get(normalizeCaseSlug(slug));
  return Number.isInteger(idx) ? idx : -1;
}

export function getCaseByName(name) {
  return state.caseByNormalizedName.get(normalizeCaseIdentityName(name))?.caseObj || null;
}

export function getCaseIndexByName(name) {
  const idx = state.caseByNormalizedName.get(normalizeCaseIdentityName(name))?.idx;
  return Number.isInteger(idx) ? idx : -1;
}

export function setFilteredResults(results) {
  state.filteredResults = Array.isArray(results) ? results : [];
  state.resultByIdx.clear();
  state.filteredResults.forEach(result => {
    if (result && Number.isInteger(result.idx)) state.resultByIdx.set(result.idx, result);
  });
  return state.filteredResults;
}

export function getFilteredResult(idx) {
  return state.resultByIdx.get(Number(idx)) || null;
}

export function setSelectedComponent(kind, specs = null) {
  const stateKey = COMPONENT_STATE_KEYS[kind];
  if (!stateKey) return null;

  if (!specs) {
    state[stateKey] = null;
    exposeLegacyWindowState(stateKey, null);
    return null;
  }

  const clean = clonePlain(specs);
  const entry = {
    ...clean,
    customized: false,
    original_specs: clonePlain(clean)
  };

  state[stateKey] = entry;
  exposeLegacyWindowState(stateKey, entry);
  return entry;
}

export function markSelectedComponentCustomized(kind, effectivePatch = {}) {
  const stateKey = COMPONENT_STATE_KEYS[kind];
  if (!stateKey) return null;
  const current = state[stateKey];
  if (!current) return null;

  const updated = {
    ...current,
    ...effectivePatch,
    customized: true,
    original_specs: current.original_specs || clonePlain(current)
  };

  state[stateKey] = updated;
  exposeLegacyWindowState(stateKey, updated);
  return updated;
}

export function clearSelectedComponent(kind) {
  return setSelectedComponent(kind, null);
}

export function clearAllSelectedComponents() {
  Object.keys(COMPONENT_STATE_KEYS).forEach(clearSelectedComponent);
}

export function getSelectedComponent(kind) {
  const stateKey = COMPONENT_STATE_KEYS[kind];
  return stateKey ? state[stateKey] : null;
}

/**
 * Returns exact catalog specifications only while the user has not manually
 * altered the selected component's physical requirements. This prevents an
 * exact model identity being sent alongside dimensions that no longer describe
 * that model.
 */
export function getExactSelectedComponentSpecs(kind) {
  const selected = getSelectedComponent(kind);
  if (!selected || selected.customized) return null;

  const { original_specs, customized, ...flat } = selected;
  return clonePlain(original_specs || flat);
}

export function getSelectedComponentIdentity(kind) {
  const selected = getSelectedComponent(kind);
  if (!selected) return null;
  return {
    name: selected.name || selected.model_query || null,
    catalogId: selected.customized ? null : (selected.catalogId || selected.catalog_id || selected.id || null),
    customized: !!selected.customized,
    original_specs: clonePlain(selected.original_specs || null)
  };
}

export function normalizeCaseIdentityName(value) {
  return String(value || '')
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export function getCaseStateKey(caseObj) {
  if (!caseObj) return '';

  // Trust only namespaced internal keys from the new identity contract. Legacy raw
  // _case_key/route slugs can be lossy and may legitimately collide.
  const existing = String(caseObj._stableKey || caseObj._case_key || caseObj.case_key || '').trim().toLowerCase();
  if (existing.startsWith('id:') || existing.startsWith('name:')) return existing;

  const explicit = caseObj.catalogId ?? caseObj.catalog_id ?? caseObj.case_id ?? caseObj.caseId ?? caseObj.id ?? caseObj.case_uuid ?? caseObj.uuid;
  if (explicit !== null && explicit !== undefined && String(explicit).trim() !== '') {
    return `id:${String(explicit).normalize('NFKC').trim().toLowerCase()}`;
  }

  const nameKey = normalizeCaseIdentityName(caseObj.name || caseObj.title || '');
  return nameKey ? `name:${nameKey}` : '';
}

export function getCaseModeSelection(caseObj, fallback = 'default') {
  const stableKey = getCaseStateKey(caseObj);
  if (stableKey && state.caseModeSelections[stableKey]) return state.caseModeSelections[stableKey];
  if (caseObj?.name && state.caseModeSelections[caseObj.name]) {
    const legacy = state.caseModeSelections[caseObj.name];
    if (stableKey) state.caseModeSelections[stableKey] = legacy;
    return legacy;
  }
  return fallback;
}

export function setCaseModeSelection(caseObj, modeId) {
  const stableKey = getCaseStateKey(caseObj);
  if (!stableKey) return;
  state.caseModeSelections[stableKey] = modeId || 'default';
}
